/**
 * Agent Editor
 * Full voice agent configuration for all providers
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Save, ArrowLeft, Mic, Bot, Settings, Phone, Zap,
  Volume2, Globe, Cpu, Thermometer, Clock, AlertCircle, X, ClipboardList, Key
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import FunctionModal from '../components/FunctionModal';
import { useAuth } from '../context/AuthContext';

// Provider info
const PROVIDERS = {
  openai: {
    name: 'OpenAI Realtime',
    description: 'GPT-4o with native voice support',
    icon: '🧠',
    color: 'bg-green-100 text-green-800',
    costPerMin: '$0.12'
  },
  deepgram: {
    name: 'Deepgram',
    description: 'Nova-2 STT + Aura/OpenAI TTS',
    icon: '🎙️',
    color: 'bg-blue-100 text-blue-800',
    costPerMin: '$0.01'
  },
  custom: {
    name: 'Custom Voice',
    description: 'Soniox STT + Groq LLM + ElevenLabs TTS',
    icon: '⚡',
    color: 'bg-purple-100 text-purple-800',
    costPerMin: '$0.005'
  },
  'intent-ivr': {
    name: 'Intent IVR',
    description: 'Pre-recorded audio with intent matching',
    icon: '📞',
    color: 'bg-amber-100 text-amber-800',
    costPerMin: '$0.001'
  },
  livekit: { 
    name: 'LiveKit', 
    icon: '🎬', 
    desc: 'Self-hosted LiveKit Agents',
    color: 'bg-cyan-100 text-cyan-800'
  }
};

// Voice options
const KOKORO_VOICES = [
  { id: 'af_sarah', name: 'Sarah', desc: 'American Female' },
  { id: 'af_bella', name: 'Bella', desc: 'American Female' },
  { id: 'af_nicole', name: 'Nicole', desc: 'American Female' },
  { id: 'af_nova', name: 'Nova', desc: 'American Female' },
  { id: 'af_sky', name: 'Sky', desc: 'American Female' },
  { id: 'af_river', name: 'River', desc: 'American Female' },
  { id: 'am_adam', name: 'Adam', desc: 'American Male' },
  { id: 'am_echo', name: 'Echo', desc: 'American Male' },
  { id: 'am_eric', name: 'Eric', desc: 'American Male' },
  { id: 'am_liam', name: 'Liam', desc: 'American Male' },
  { id: 'am_michael', name: 'Michael', desc: 'American Male' },
  { id: 'bf_emma', name: 'Emma', desc: 'British Female' },
  { id: 'bf_isabella', name: 'Isabella', desc: 'British Female' },
  { id: 'bm_george', name: 'George', desc: 'British Male' },
  { id: 'bm_daniel', name: 'Daniel', desc: 'British Male' },
];

const OPENAI_VOICES = [
  { id: 'alloy', name: 'Alloy', desc: 'Neutral' },
  { id: 'ash', name: 'Ash', desc: 'Warm Male' },
  { id: 'ballad', name: 'Ballad', desc: 'Expressive' },
  { id: 'coral', name: 'Coral', desc: 'Friendly Female' },
  { id: 'echo', name: 'Echo', desc: 'Professional Male' },
  { id: 'sage', name: 'Sage', desc: 'Calm Female' },
  { id: 'shimmer', name: 'Shimmer', desc: 'Clear Female' },
  { id: 'verse', name: 'Verse', desc: 'Conversational' },
  { id: 'marin', name: 'Marin', desc: 'Soft Female' },
  { id: 'cedar', name: 'Cedar', desc: 'Deep Male' }
];

const DEEPGRAM_VOICES = [
  // English voices
  { id: 'aura-asteria-en', name: 'Asteria', desc: 'English Female', lang: 'en' },
  { id: 'aura-luna-en', name: 'Luna', desc: 'English Female', lang: 'en' },
  { id: 'aura-stella-en', name: 'Stella', desc: 'English Female', lang: 'en' },
  { id: 'aura-athena-en', name: 'Athena', desc: 'English Female', lang: 'en' },
  { id: 'aura-hera-en', name: 'Hera', desc: 'English Female', lang: 'en' },
  { id: 'aura-orion-en', name: 'Orion', desc: 'English Male', lang: 'en' },
  { id: 'aura-arcas-en', name: 'Arcas', desc: 'English Male', lang: 'en' },
  { id: 'aura-perseus-en', name: 'Perseus', desc: 'English Male', lang: 'en' },
  { id: 'aura-angus-en', name: 'Angus', desc: 'Irish Male', lang: 'en' },
  { id: 'aura-orpheus-en', name: 'Orpheus', desc: 'English Male', lang: 'en' },
  { id: 'aura-helios-en', name: 'Helios', desc: 'British Male', lang: 'en' },
  { id: 'aura-zeus-en', name: 'Zeus', desc: 'English Male', lang: 'en' }
];

const LIVEKIT_VOICES = [
  { id: 'alloy', name: 'Alloy', desc: 'Neutral' },
  { id: 'echo', name: 'Echo', desc: 'Male' },
  { id: 'fable', name: 'Fable', desc: 'Expressive' },
  { id: 'onyx', name: 'Onyx', desc: 'Deep' },
  { id: 'nova', name: 'Nova', desc: 'Female' },
  { id: 'shimmer', name: 'Shimmer', desc: 'Soft' }
];

const LIVEKIT_STT_PROVIDERS = [
  { id: 'deepgram', name: 'Deepgram' },
  { id: 'azure', name: 'Azure Speech' },
  { id: 'google', name: 'Google Cloud' }
];

const LIVEKIT_TTS_PROVIDERS = [
  { id: 'openai', name: 'OpenAI' },
  { id: 'elevenlabs', name: 'ElevenLabs' },
  { id: 'azure', name: 'Azure Speech' },
  { id: 'cartesia', name: 'Cartesia' },
  { id: 'deepgram', name: 'Deepgram Aura' }
];

const CARTESIA_VOICES = [
  // English Female
  { id: 'a0e99841-438c-4a64-b679-ae501e7d6091', name: 'Barbershop Man', desc: 'Male, Conversational' },
  { id: '79a125e8-cd45-4c13-8a67-188112f4dd22', name: 'British Lady', desc: 'Female, British' },
  { id: 'b7d50908-b17c-442d-ad8d-810c63997ed9', name: 'California Girl', desc: 'Female, Casual' },
  { id: '5619d38c-cf51-4d8e-9575-48f61a280571', name: 'Classy British Man', desc: 'Male, British' },
  { id: 'c45bc5ec-dc68-4feb-8829-6e6b2748095d', name: 'Confident British Man', desc: 'Male, British' },
  { id: '63ff761f-c1e8-414b-b969-d1833d1c870c', name: 'Friendly Australian Man', desc: 'Male, Australian' },
  { id: 'fb26447f-308b-471e-8b00-8e9f04284eb5', name: 'Helpful Woman', desc: 'Female, Helpful' },
  { id: 'a167e0f3-df7e-4d52-a9c3-f949145efdab', name: 'Hindi Woman', desc: 'Female, Hindi' },
  { id: '248be419-c632-4f23-adf1-5324ed7dbf1d', name: 'Hollywood Man', desc: 'Male, Dramatic' },
  { id: 'e3827ec5-697a-4b7c-9704-1a23041bbc51', name: 'Maria', desc: 'Female, Spanish' },
  { id: '573e3144-a684-4e72-ac2b-9b2063a50b53', name: 'Midwestern Woman', desc: 'Female, Midwest' },
  { id: 'a3520a8f-226a-428d-9f2c-689f3bd0c4ae', name: 'Newsman', desc: 'Male, Professional' },
  { id: 'bf991597-6c13-47e4-8411-91ec2de5c466', name: 'Newslady', desc: 'Female, Professional' },
  { id: '7360f116-6306-4e9a-b487-1235f35a0f21', name: 'Reading Lady', desc: 'Female, Narrator' },
  { id: '421b3369-f63f-4b03-8980-37a44df1d4e8', name: 'Reflective Woman', desc: 'Female, Thoughtful' },
  { id: 'a01c369f-6d2d-4f0e-9222-7cd4b304a26f', name: 'Southern Belle', desc: 'Female, Southern' },
  { id: 'c2ac25f9-ecc4-4f56-9095-651354df60c0', name: 'Southern Man', desc: 'Male, Southern' },
  { id: '50d6beb4-80ea-4802-8387-6c948fe84208', name: 'Sweet Lady', desc: 'Female, Warm' },
  { id: 'ed81fd13-2016-4a49-8fe3-c0d2761695fc', name: 'Wise Guide Man', desc: 'Male, Wise' },
  { id: '15a9cd88-84b0-4a8b-95f2-5d583b54c72e', name: 'Wise Lady', desc: 'Female, Wise' },
  { id: 'f114a467-c40a-4db8-964d-aaba89cd08fa', name: 'Young Woman', desc: 'Female, Young' },
];

const AZURE_TTS_VOICES = [
  // English (US)
  { id: 'en-US-JennyNeural', name: 'Jenny', desc: 'US Female' },
  { id: 'en-US-GuyNeural', name: 'Guy', desc: 'US Male' },
  { id: 'en-US-AriaNeural', name: 'Aria', desc: 'US Female' },
  { id: 'en-US-DavisNeural', name: 'Davis', desc: 'US Male' },
  { id: 'en-US-AmberNeural', name: 'Amber', desc: 'US Female' },
  { id: 'en-US-AnaNeural', name: 'Ana', desc: 'US Female Child' },
  { id: 'en-US-BrandonNeural', name: 'Brandon', desc: 'US Male' },
  { id: 'en-US-ChristopherNeural', name: 'Christopher', desc: 'US Male' },
  { id: 'en-US-CoraNeural', name: 'Cora', desc: 'US Female' },
  { id: 'en-US-ElizabethNeural', name: 'Elizabeth', desc: 'US Female' },
  // English (UK)
  { id: 'en-GB-SoniaNeural', name: 'Sonia', desc: 'UK Female' },
  { id: 'en-GB-RyanNeural', name: 'Ryan', desc: 'UK Male' },
  { id: 'en-GB-LibbyNeural', name: 'Libby', desc: 'UK Female' },
  // English (Australia)
  { id: 'en-AU-NatashaNeural', name: 'Natasha', desc: 'AU Female' },
  { id: 'en-AU-WilliamNeural', name: 'William', desc: 'AU Male' },
  // English (India)
  { id: 'en-IN-NeerjaNeural', name: 'Neerja', desc: 'IN Female' },
  { id: 'en-IN-PrabhatNeural', name: 'Prabhat', desc: 'IN Male' },
];

const ELEVENLABS_VOICES = [
  // Female voices
  { id: 'Rachel', name: 'Rachel', desc: 'Calm American Female' },
  { id: 'Domi', name: 'Domi', desc: 'Strong American Female' },
  { id: 'Bella', name: 'Bella', desc: 'Soft American Female' },
  { id: 'Elli', name: 'Elli', desc: 'Emotional Young Female' },
  { id: 'Nicole', name: 'Nicole', desc: 'Soft Whisper Female' },
  { id: 'Sarah', name: 'Sarah', desc: 'Soft News Female' },
  { id: 'Emily', name: 'Emily', desc: 'Calm American Female' },
  { id: 'Emilia', name: 'Emilia', desc: 'Calm American Female' },
  { id: 'Freya', name: 'Freya', desc: 'Expressive American Female' },
  { id: 'Grace', name: 'Grace', desc: 'Southern American Female' },
  { id: 'Dorothy', name: 'Dorothy', desc: 'Pleasant British Female' },
  { id: 'Glinda', name: 'Glinda', desc: 'Witch-like American Female' },
  { id: 'Mimi', name: 'Mimi', desc: 'Animated Swedish Female' },
  { id: 'Serena', name: 'Serena', desc: 'Pleasant American Female' },
  { id: 'Gigi', name: 'Gigi', desc: 'Childish American Female' },
  { id: 'Charlotte', name: 'Charlotte', desc: 'Seductive Swedish Female' },
  { id: 'Alice', name: 'Alice', desc: 'Confident British Female' },
  { id: 'Matilda', name: 'Matilda', desc: 'Warm American Female' },
  { id: 'Lily', name: 'Lily', desc: 'British Narrator Female' },
  { id: 'Aria', name: 'Aria', desc: 'Expressive American Female' },
  { id: 'Jessica', name: 'Jessica', desc: 'Expressive American Female' },
  { id: 'Laura', name: 'Laura', desc: 'Upbeat American Female' },
  { id: 'River', name: 'River', desc: 'Confident American Non-binary' },
  // Male voices
  { id: 'Antoni', name: 'Antoni', desc: 'Well-rounded American Male' },
  { id: 'Josh', name: 'Josh', desc: 'Deep American Male' },
  { id: 'Arnold', name: 'Arnold', desc: 'Crisp American Male' },
  { id: 'Adam', name: 'Adam', desc: 'Deep American Male' },
  { id: 'Sam', name: 'Sam', desc: 'Raspy American Male' },
  { id: 'Clyde', name: 'Clyde', desc: 'War Veteran Male' },
  { id: 'George', name: 'George', desc: 'Warm British Male' },
  { id: 'Charlie', name: 'Charlie', desc: 'Casual Australian Male' },
  { id: 'James', name: 'James', desc: 'Australian Narrator Male' },
  { id: 'Callum', name: 'Callum', desc: 'Intense Transatlantic Male' },
  { id: 'Patrick', name: 'Patrick', desc: 'Shouty American Male' },
  { id: 'Harry', name: 'Harry', desc: 'Anxious American Male' },
  { id: 'Liam', name: 'Liam', desc: 'Articulate American Male' },
  { id: 'Daniel', name: 'Daniel', desc: 'Deep British Male' },
  { id: 'Bill', name: 'Bill', desc: 'Documentary American Male' },
  { id: 'Fin', name: 'Fin', desc: 'Friendly Irish Male' },
  { id: 'Brian', name: 'Brian', desc: 'Deep American Male' },
  { id: 'Eric', name: 'Eric', desc: 'Friendly American Male' },
  { id: 'Chris', name: 'Chris', desc: 'Casual American Male' },
  { id: 'Will', name: 'Will', desc: 'Friendly American Male' },
  { id: 'Michael', name: 'Michael', desc: 'Smooth American Male' },
  { id: 'Ethan', name: 'Ethan', desc: 'Soft American Male' },
  { id: 'Marcus', name: 'Marcus', desc: 'Authoritative American Male' },
  // Narration/Special voices
  { id: 'Drew', name: 'Drew', desc: 'Well-rounded American Male' },
  { id: 'Paul', name: 'Paul', desc: 'Ground News American Male' },
  { id: 'Thomas', name: 'Thomas', desc: 'Calm American Male' },
  { id: 'Dave', name: 'Dave', desc: 'Conversational British-Essex Male' },
];

const LANGUAGES = [
  // English variants
  { code: 'en', name: 'English' },
  { code: 'en-US', name: 'English (US)' },
  { code: 'en-GB', name: 'English (UK)' },
  { code: 'en-AU', name: 'English (Australia)' },
  { code: 'en-IN', name: 'English (India)' },
  // South Asian
  { code: 'ur', name: 'Urdu' },
  { code: 'ur-PK', name: 'Urdu (Pakistan)' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hi-IN', name: 'Hindi (India)' },
  { code: 'bn', name: 'Bengali' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' },
  { code: 'mr', name: 'Marathi' },
  { code: 'gu', name: 'Gujarati' },
  // Arabic variants
  { code: 'ar', name: 'Arabic' },
  { code: 'ar-SA', name: 'Arabic (Saudi)' },
  { code: 'ar-EG', name: 'Arabic (Egypt)' },
  { code: 'ar-AE', name: 'Arabic (UAE)' },
  // European
  { code: 'es', name: 'Spanish' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'es-419', name: 'Spanish (Latin America)' },
  { code: 'fr', name: 'French' },
  { code: 'fr-FR', name: 'French (France)' },
  { code: 'fr-CA', name: 'French (Canada)' },
  { code: 'de', name: 'German' },
  { code: 'de-DE', name: 'German (Germany)' },
  { code: 'de-AT', name: 'German (Austria)' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)' },
  { code: 'it', name: 'Italian' },
  { code: 'nl', name: 'Dutch' },
  { code: 'pl', name: 'Polish' },
  { code: 'ru', name: 'Russian' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'cs', name: 'Czech' },
  { code: 'el', name: 'Greek' },
  { code: 'ro', name: 'Romanian' },
  { code: 'hu', name: 'Hungarian' },
  // Nordic
  { code: 'sv', name: 'Swedish' },
  { code: 'da', name: 'Danish' },
  { code: 'no', name: 'Norwegian' },
  { code: 'fi', name: 'Finnish' },
  // Asian
  { code: 'zh', name: 'Chinese (Mandarin)' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'th', name: 'Thai' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'id', name: 'Indonesian' },
  { code: 'ms', name: 'Malay' },
  { code: 'tl', name: 'Tagalog (Filipino)' },
  // Middle Eastern
  { code: 'tr', name: 'Turkish' },
  { code: 'he', name: 'Hebrew' },
  { code: 'fa', name: 'Persian (Farsi)' },
  // African
  { code: 'sw', name: 'Swahili' },
  { code: 'af', name: 'Afrikaans' },
  { code: 'ha', name: 'Hausa' },
  { code: 'ig', name: 'Igbo' },
  { code: 'ful', name: 'Fulah' },
  { code: 'lug', name: 'Ganda' },
  { code: 'kea', name: 'Kabuverdianu' },
  // European (additional)
  { code: 'et', name: 'Estonian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'gl', name: 'Galician' },
  { code: 'el', name: 'Greek' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'is', name: 'Icelandic' },
  { code: 'gle', name: 'Irish' },
  { code: 'lv', name: 'Latvian' },
  // South/Southeast Asian (additional)
  { code: 'jav', name: 'Javanese' },
  { code: 'km', name: 'Khmer' },
  { code: 'lo', name: 'Lao' },
  // Caucasus / Central Asia
  { code: 'ka', name: 'Georgian' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'ky', name: 'Kyrgyz' },
  // Middle Eastern (additional)
  { code: 'he', name: 'Hebrew' },
  { code: 'ku', name: 'Kurdish' },
  // Multi
  { code: 'multi', name: 'Multi-language (Auto-detect)' }
];

// ElevenLabs supported language codes for eleven_turbo_v2_5 model
const ELEVENLABS_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'ar', name: 'Arabic' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'de', name: 'German' },
  { code: 'el', name: 'Greek' },
  { code: 'es', name: 'Spanish' },
  { code: 'et', name: 'Estonian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'fil', name: 'Filipino' },
  { code: 'fr', name: 'French' },
  { code: 'ga', name: 'Irish' },
  { code: 'gl', name: 'Galician' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hr', name: 'Croatian' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'id', name: 'Indonesian' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'lv', name: 'Latvian' },
  { code: 'ms', name: 'Malay' },
  { code: 'nl', name: 'Dutch' },
  { code: 'no', name: 'Norwegian' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'sr', name: 'Serbian' },
  { code: 'sv', name: 'Swedish' },
  { code: 'sw', name: 'Swahili' },
  { code: 'tr', name: 'Turkish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ur', name: 'Urdu' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'zh', name: 'Chinese' },
];

// The live model list comes from GET /api/agents/llm-models (source of truth:
// api/src/config/llm-catalog.js). These constants are only the offline fallback
// for when that request fails — do not add models here alone or they will not
// validate server-side.
const FALLBACK_LLM_MODELS = [
  { id: 'openai/gpt-oss-120b', name: 'GPT-OSS 120B', provider: 'groq', status: 'production' },
  { id: 'openai/gpt-oss-20b', name: 'GPT-OSS 20B', provider: 'groq', status: 'production' },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'openai', status: 'legacy' },
  { id: 'gpt-4o', name: 'GPT-4o', provider: 'openai', status: 'legacy' },
  { id: 'llama3.1:8b-instruct-q4_K_M', name: 'Llama 3.1 8B Q4 (Local)', provider: 'ollama', status: 'production' },
];

const FALLBACK_LLM_DEFAULTS = {
  groq: 'openai/gpt-oss-20b',
  openai: 'gpt-4o-mini',
  ollama: 'llama3.1:8b-instruct-q4_K_M',
};

export default function AgentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const { user: currentUser } = useAuth();
  const isReadOnly = currentUser?.role === 'viewer';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [agent, setAgent] = useState({
    name: '',
    type: 'custom',
    provider: 'openai',
    instructions: '',
    greeting: '',
    // OpenAI
    voice: 'shimmer',
    model: 'gpt-4o-mini-realtime-preview-2024-12-17',
    language: 'en',
    // Deepgram
    deepgram_model: 'nova-3',
    deepgram_voice: 'shimmer',
    deepgram_language: 'en',
    // Custom
    tts_provider: 'elevenlabs',
	tts_language: 'en',
    custom_voice: 'Rachel',
    language_hints: ['ur', 'en'],
    llm_model: FALLBACK_LLM_DEFAULTS.groq,
	llm_provider: 'groq',
	ollama_base_url: '',
	kokoro_base_url: '',
	stt_provider: 'soniox',
	stt_url: '',
    // Settings
    temperature: 0.6,
    max_tokens: 4096,
    vad_threshold: 0.5,
    silence_duration_ms: 500,
    is_active: true,
    // Outbound / AMD settings
    amd_enabled: false,
    amd_action: 'hangup',
    amd_message: '',
    amd_timeout: 8000,
	livekit_url: '',
    livekit_agent_name: 'voice-agent',
    livekit_voice: 'alloy',
    livekit_model: 'gpt-4o-mini',
    livekit_stt_provider: 'deepgram',
    livekit_tts_provider: 'openai',
	livekit_language: 'en',
	api_keys: {},
  });

  const [functions, setFunctions] = useState([]);
  const [showFunctionModal, setShowFunctionModal] = useState(false);
  const [editingFunction, setEditingFunction] = useState(null);

  // LLM catalog, fetched from the API so retired models disappear from the
  // picker without a dashboard release.
  const [llmModels, setLlmModels] = useState(FALLBACK_LLM_MODELS);
  const [llmDefaults, setLlmDefaults] = useState(FALLBACK_LLM_DEFAULTS);
  const [retiredModels, setRetiredModels] = useState({});

  useEffect(() => {
    if (id) {
      loadAgent();
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.getLlmModels();
        if (cancelled || !data?.models?.length) return;
        setLlmModels(data.models);
        setLlmDefaults(data.defaults || FALLBACK_LLM_DEFAULTS);
        setRetiredModels(data.retired || {});
      } catch (err) {
        // Non-fatal: the bundled fallback list keeps the editor usable.
        console.warn('Could not load LLM catalog, using bundled fallback', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadAgent = async () => {
    try {
      setLoading(true);
      const response = await api.getAgent(id);
      const loadedAgent = response.data.agent;
      
      // Set provider-specific defaults
      if (loadedAgent.provider === 'openai') {
        setAgent({
          ...loadedAgent,
          voice: loadedAgent.voice || 'shimmer',
          model: loadedAgent.model || 'gpt-4o-mini-realtime-preview-2024-12-17',
          language: loadedAgent.language || 'en'
        });
      } else if (loadedAgent.provider === 'deepgram') {
        setAgent({
          ...loadedAgent,
          deepgram_model: loadedAgent.deepgram_model || 'nova-3',
          deepgram_voice: loadedAgent.deepgram_voice || 'shimmer',
          deepgram_language: loadedAgent.deepgram_language || 'en'
        });
      } else if (loadedAgent.provider === 'custom') {
        setAgent({
			...loadedAgent,
			tts_provider: loadedAgent.tts_provider || 'elevenlabs',
			tts_language: loadedAgent.tts_language || 'en',
			custom_voice: loadedAgent.custom_voice || 'Rachel',
			language_hints: loadedAgent.language_hints || ['ur', 'en'],
			llm_provider: loadedAgent.llm_provider || 'groq',
			llm_model: loadedAgent.llm_model || llmDefaults[loadedAgent.llm_provider || 'groq'],
			ollama_base_url: loadedAgent.ollama_base_url || '',
			kokoro_base_url: loadedAgent.kokoro_base_url || '',
			stt_provider: loadedAgent.stt_provider || 'soniox',
			stt_url: loadedAgent.stt_url || '',
        });
      } else if (loadedAgent.provider === 'intent-ivr') {
        setAgent({
          ...loadedAgent,
          tts_provider: loadedAgent.tts_provider || 'elevenlabs',
          custom_voice: loadedAgent.custom_voice || 'Rachel',
          language_hints: loadedAgent.language_hints || ['ur', 'en']
        });
	  } else if (loadedAgent.provider === 'livekit') {
		  setAgent({
			...loadedAgent,
			livekit_url: loadedAgent.livekit_url || '',
			livekit_agent_name: loadedAgent.livekit_agent_name || 'voice-agent',
			livekit_voice: loadedAgent.livekit_voice || 'alloy',
			livekit_model: loadedAgent.livekit_model || 'gpt-4o-mini',
			livekit_stt_provider: loadedAgent.livekit_stt_provider || 'deepgram',
			livekit_tts_provider: loadedAgent.livekit_tts_provider || 'openai',
			livekit_language: loadedAgent.livekit_language || 'en',
			language_hints: loadedAgent.language_hints || ['en']
		  });
      } else {
        setAgent({
          ...loadedAgent,
          provider: 'openai',
          voice: loadedAgent.voice || 'shimmer',
          model: loadedAgent.model || 'gpt-4o-mini-realtime-preview-2024-12-17'
        });
      }
      
      // Load functions separately
      try {
        const funcRes = await api.get(`/functions/agent/${id}`);
        if (funcRes.data.functions) {
          setFunctions(funcRes.data.functions);
        }
      } catch (funcError) {
        console.error('Failed to load functions:', funcError);
      }
    } catch (error) {
      toast.error('Failed to load agent');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (newProvider) => {
    if (newProvider === 'openai') {
      setAgent({
        ...agent,
        provider: newProvider,
        voice: agent.voice || 'shimmer',
        model: agent.model || 'gpt-4o-mini-realtime-preview-2024-12-17',
        language: agent.language || 'en'
      });
    } else if (newProvider === 'deepgram') {
      setAgent({
        ...agent,
        provider: newProvider,
        deepgram_model: agent.deepgram_model || 'nova-3',
        deepgram_voice: agent.deepgram_voice || 'shimmer',
        deepgram_language: agent.deepgram_language || 'en'
      });
    } else if (newProvider === 'custom') {
      setAgent({
		...agent,
		provider: newProvider,
		tts_provider: agent.tts_provider || 'elevenlabs',
		custom_voice: agent.custom_voice || 'Rachel',
		language_hints: agent.language_hints || ['ur', 'en'],
		llm_provider: agent.llm_provider || 'groq',
		llm_model: agent.llm_model || llmDefaults[agent.llm_provider || 'groq'],
		ollama_base_url: agent.ollama_base_url || '',
		kokoro_base_url: agent.kokoro_base_url || '',
		stt_provider: agent.stt_provider || 'soniox',
		stt_url: agent.stt_url || '',
	  });
    } else if (newProvider === 'intent-ivr') {
      setAgent({
        ...agent,
        provider: newProvider,
        tts_provider: agent.tts_provider || 'elevenlabs',
        custom_voice: agent.custom_voice || 'Rachel',
        language_hints: agent.language_hints || ['ur', 'en']
      });
	} else if (newProvider === 'livekit') {
	  setAgent({
		...agent,
		provider: newProvider,
		livekit_url: agent.livekit_url || '',
		livekit_agent_name: agent.livekit_agent_name || 'voice-agent',
		livekit_voice: agent.livekit_voice || 'alloy',
		livekit_model: agent.livekit_model || 'gpt-4o-mini',
		livekit_stt_provider: agent.livekit_stt_provider || 'deepgram',
		livekit_tts_provider: agent.livekit_tts_provider || 'openai',
		livekit_language: agent.livekit_language || 'en',
		language_hints: agent.language_hints || ['en']
	  });
	}
  };

  const getLivekitVoices = () => {
	  switch (agent.livekit_tts_provider) {
		case 'openai':
		  return LIVEKIT_VOICES;
		case 'elevenlabs':
		  return ELEVENLABS_VOICES;
		case 'cartesia':
		  return CARTESIA_VOICES;
		case 'azure':
		  return AZURE_TTS_VOICES;
		case 'deepgram':
		  return DEEPGRAM_VOICES;
		default:
		  return LIVEKIT_VOICES;
	  }
	};

	// Get default voice for TTS provider
	const getDefaultVoiceForTTSProvider = (ttsProvider) => {
	  switch (ttsProvider) {
		case 'openai':
		  return 'alloy';
		case 'elevenlabs':
		  return 'Rachel';
		case 'cartesia':
		  return 'fb26447f-308b-471e-8b00-8e9f04284eb5'; // Helpful Woman
		case 'azure':
		  return 'en-US-JennyNeural';
		case 'deepgram':
		  return 'aura-asteria-en';
		default:
		  return 'alloy';
	  }
	};

	// Handle TTS provider change - reset voice to default for new provider
	const handleLivekitTTSProviderChange = (newTTSProvider) => {
	  setAgent({
		...agent,
		livekit_tts_provider: newTTSProvider,
		livekit_voice: getDefaultVoiceForTTSProvider(newTTSProvider)
	  });
	};

	const handleCustomTTSProviderChange = (newTTSProvider) => {
	  const defaultVoices = {
		elevenlabs: 'Rachel',
		deepgram: 'aura-asteria-en',
		openai: 'shimmer',
		azure: 'en-US-JennyNeural',
		kokoro: 'af_sarah'
	  };
	  setAgent({
		...agent,
		tts_provider: newTTSProvider,
		custom_voice: defaultVoices[newTTSProvider] || 'Rachel'
	  });
	};

  const handleSave = async () => {
    if (isReadOnly) {
      toast.error('Read-only access — you cannot save changes');
      return;
    }
    if (!agent.name) {
      toast.error('Please enter an agent name');
      return;
    }
    
    try {
      setSaving(true);
      
      if (isNew) {
        const response = await api.createAgent(agent);
        toast.success('Agent created successfully');
        navigate(`/agents/${response.data.agent.id}`);
      } else {
        await api.updateAgent(id, agent);
        toast.success('Agent updated successfully');
      }
    } catch (error) {
      toast.error('Failed to save agent');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const toggleLanguageHint = (langCode) => {
    const hints = agent.language_hints || [];
    if (hints.includes(langCode)) {
      setAgent({
        ...agent,
        language_hints: hints.filter(l => l !== langCode)
      });
    } else {
      setAgent({
        ...agent,
        language_hints: [...hints, langCode]
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const providerInfo = PROVIDERS[agent.provider] || PROVIDERS.openai;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/agents" className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isNew ? 'Create Voice Agent' : 'Edit Voice Agent'}
            </h1>
            <p className="text-sm text-gray-500">
              Configure your AI voice agent
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isNew && !isReadOnly && agent.provider !== 'intent-ivr' && (
            <Link
              to={`/test?agent=${id}`}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              Test Voice
            </Link>
          )}
          {!isNew && agent.provider === 'intent-ivr' && (
            <Link
              to={`/agents/${id}/ivr`}
              className="px-4 py-2 border border-amber-300 bg-amber-50 rounded-lg text-amber-700 hover:bg-amber-100 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              IVR Config
            </Link>
          )}
          {!isReadOnly && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Agent'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {[
            { id: 'basic', label: 'Basic Info', icon: Bot },
            { id: 'voice', label: 'Voice Config', icon: Volume2 },
            { id: 'behavior', label: 'Behavior', icon: Cpu },
            { id: 'outbound', label: 'Outbound', icon: Phone },
            { id: 'functions', label: 'Functions', icon: Zap },
            ...(isReadOnly ? [] : [{ id: 'apikeys', label: 'API Keys', icon: Key }])
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {isReadOnly && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2 text-sm">
          Read-only mode — you can review the agent configuration but cannot make changes.
        </div>
      )}
      <fieldset disabled={isReadOnly} className={isReadOnly ? 'opacity-90 pointer-events-none' : ''}>
      <div className="bg-white rounded-lg shadow">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Agent Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agent Name *
                </label>
                <input
                  type="text"
                  value={agent.name}
                  onChange={(e) => setAgent({ ...agent, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Sales Assistant"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agent Type
                </label>
                <select
                  value={agent.type}
                  onChange={(e) => setAgent({ ...agent, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="sales">Sales</option>
                  <option value="support">Support</option>
                  <option value="banking">Banking</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>

            {/* Provider Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Voice Provider
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(PROVIDERS).map(([key, provider]) => (
                  <button
                    key={key}
                    onClick={() => handleProviderChange(key)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      agent.provider === key
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{provider.icon}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${provider.color}`}>
                        {provider.costPerMin}/min
                      </span>
                    </div>
                    <div className="font-medium text-gray-900">{provider.name}</div>
                    <div className="text-xs text-gray-500 mt-1">{provider.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setAgent({ ...agent, is_active: !agent.is_active })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  agent.is_active ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    agent.is_active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-700">
                Agent is {agent.is_active ? 'active' : 'inactive'}
              </span>
            </div>

            {/* Greeting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Greeting Message
              </label>
              <input
                type="text"
                value={agent.greeting || ''}
                onChange={(e) => setAgent({ ...agent, greeting: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Hello! How can I help you today?"
              />
              <p className="text-xs text-gray-500 mt-1">
                First message the agent speaks when a call connects
              </p>
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent Instructions
              </label>
              {isReadOnly ? (
                <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-600">
                  {agent.instructions
                    ? (
                        <>
                          <div className="line-clamp-2 whitespace-pre-wrap">
                            {agent.instructions.split('\n').slice(0, 2).join('\n')}
                          </div>
                          <p className="text-xs text-gray-400 italic mt-2">
                            Full script hidden in read-only mode
                          </p>
                        </>
                      )
                    : <span className="text-gray-400 italic">No instructions set</span>}
                </div>
              ) : (
                <textarea
                  value={agent.instructions || ''}
                  onChange={(e) => setAgent({ ...agent, instructions: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="You are a helpful voice assistant..."
                />
              )}
              <p className="text-xs text-gray-500 mt-1">
                System prompt that defines agent behavior and personality
              </p>
            </div>
          </div>
        )}

        {/* Voice Config Tab */}
        {activeTab === 'voice' && (
          <div className="p-6 space-y-6">
            {/* OpenAI Config */}
            {agent.provider === 'openai' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Model
                    </label>
                    <select
                      value={agent.model}
                      onChange={(e) => setAgent({ ...agent, model: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="gpt-4o-realtime-preview-2024-12-17">GPT-4o Realtime</option>
                      <option value="gpt-4o-mini-realtime-preview-2024-12-17">GPT-4o Mini Realtime</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Language
                    </label>
                    <select
                      value={agent.language}
                      onChange={(e) => setAgent({ ...agent, language: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {LANGUAGES.map(lang => (
                        <option key={lang.code} value={lang.code}>{lang.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Voice
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {OPENAI_VOICES.map(voice => (
                      <button
                        key={voice.id}
                        onClick={() => setAgent({ ...agent, voice: voice.id })}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          agent.voice === voice.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium">{voice.name}</div>
                        <div className="text-xs text-gray-500">{voice.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Deepgram Config */}
            {agent.provider === 'deepgram' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      STT Model
                    </label>
                    <select
                      value={agent.deepgram_model}
                      onChange={(e) => setAgent({ ...agent, deepgram_model: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="nova-3">Nova-3 (Best — 60+ languages)</option>
                      <option value="nova-2">Nova-2</option>
                      <option value="nova">Nova</option>
                      <option value="enhanced">Enhanced</option>
                      <option value="base">Base</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Language
                    </label>
                    <select
                      value={agent.deepgram_language}
                      onChange={(e) => setAgent({ ...agent, deepgram_language: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <optgroup label="Multi-language">
                        <option value="multi">Multi-language (Auto-detect)</option>
                      </optgroup>
                      <optgroup label="English">
                        <option value="en">English</option>
                        <option value="en-US">English (US)</option>
                        <option value="en-GB">English (UK)</option>
                        <option value="en-AU">English (Australia)</option>
                        <option value="en-IN">English (India)</option>
                        <option value="en-NZ">English (New Zealand)</option>
                      </optgroup>
                      <optgroup label="European">
                        <option value="fr">French</option>
                        <option value="fr-CA">French (Canada)</option>
                        <option value="de">German</option>
                        <option value="de-CH">German (Switzerland)</option>
                        <option value="es">Spanish</option>
                        <option value="es-419">Spanish (Latin America)</option>
                        <option value="it">Italian</option>
                        <option value="pt">Portuguese</option>
                        <option value="pt-BR">Portuguese (Brazil)</option>
                        <option value="pt-PT">Portuguese (Portugal)</option>
                        <option value="nl">Dutch</option>
                        <option value="nl-BE">Flemish</option>
                        <option value="pl">Polish</option>
                        <option value="ru">Russian</option>
                        <option value="uk">Ukrainian</option>
                        <option value="cs">Czech</option>
                        <option value="sk">Slovak</option>
                        <option value="ro">Romanian</option>
                        <option value="bg">Bulgarian</option>
                        <option value="hr">Croatian</option>
                        <option value="bs">Bosnian</option>
                        <option value="sr">Serbian</option>
                        <option value="sl">Slovenian</option>
                        <option value="mk">Macedonian</option>
                        <option value="be">Belarusian</option>
                        <option value="ca">Catalan</option>
                        <option value="el">Greek</option>
                        <option value="hu">Hungarian</option>
                        <option value="sv">Swedish</option>
                        <option value="sv-SE">Swedish (Sweden)</option>
                        <option value="da">Danish</option>
                        <option value="no">Norwegian</option>
                        <option value="fi">Finnish</option>
                        <option value="et">Estonian</option>
                        <option value="lv">Latvian</option>
                        <option value="lt">Lithuanian</option>
                      </optgroup>
                      <optgroup label="Middle Eastern">
                        <option value="ar">Arabic</option>
                        <option value="ar-AE">Arabic (UAE)</option>
                        <option value="ar-SA">Arabic (Saudi)</option>
                        <option value="ar-EG">Arabic (Egypt)</option>
                        <option value="he">Hebrew</option>
                        <option value="fa">Persian (Farsi)</option>
                        <option value="tr">Turkish</option>
                      </optgroup>
                      <optgroup label="South Asian">
                        <option value="hi">Hindi</option>
                        <option value="ur">Urdu</option>
                        <option value="bn">Bengali</option>
                        <option value="ta">Tamil</option>
                        <option value="te">Telugu</option>
                        <option value="mr">Marathi</option>
                        <option value="kn">Kannada</option>
                      </optgroup>
                      <optgroup label="East / Southeast Asian">
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="zh">Chinese (Mandarin)</option>
                        <option value="zh-HK">Chinese (Cantonese)</option>
                        <option value="vi">Vietnamese</option>
                        <option value="th">Thai</option>
                        <option value="id">Indonesian</option>
                        <option value="ms">Malay</option>
                        <option value="tl">Tagalog (Filipino)</option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    TTS Voice
                  </label>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-2">OpenAI Voices (Recommended)</p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {OPENAI_VOICES.slice(0, 5).map(voice => (
                          <button
                            key={voice.id}
                            onClick={() => setAgent({ ...agent, deepgram_voice: voice.id })}
                            className={`p-3 rounded-lg border-2 text-center transition-all ${
                              agent.deepgram_voice === voice.id
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium">{voice.name}</div>
                            <div className="text-xs text-gray-500">{voice.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Deepgram Aura Voices</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {DEEPGRAM_VOICES.map(voice => (
                          <button
                            key={voice.id}
                            onClick={() => setAgent({ ...agent, deepgram_voice: voice.id })}
                            className={`p-3 rounded-lg border-2 text-center transition-all ${
                              agent.deepgram_voice === voice.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium">{voice.name}</div>
                            <div className="text-xs text-gray-500">{voice.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Custom Provider Config */}
            {agent.provider === 'custom' && (
              <div className="space-y-6">
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">⚡</span>
                    <h4 className="font-semibold text-purple-900">Custom Voice Stack</h4>
                  </div>
                  <p className="text-sm text-purple-700">
                    Soniox STT → Groq/OpenAI LLM → ElevenLabs/OpenAI TTS
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				  <div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  LLM Provider
					</label>
					<select
					  value={agent.llm_provider || 'groq'}
					  onChange={(e) => {
						const prov = e.target.value;
						setAgent({
						  ...agent,
						  llm_provider: prov,
						  llm_model: llmDefaults[prov] || FALLBACK_LLM_DEFAULTS[prov],
						});
					  }}
					  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					>
					  <option value="groq">Groq (Cloud)</option>
					  <option value="openai">OpenAI (Cloud)</option>
					  <option value="ollama">Ollama (Local)</option>
					</select>
				  </div>

				  <div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  LLM Model
					</label>
					<select
					  value={agent.llm_model || ''}
					  onChange={(e) => setAgent({ ...agent, llm_model: e.target.value })}
					  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					>
					  {llmModels
						.filter(m => m.provider === (agent.llm_provider || 'groq'))
						.map(model => (
						  <option key={model.id} value={model.id} title={model.description || ''}>
							{model.name}
							{model.status === 'preview' ? ' (Preview)' : ''}
							{model.status === 'legacy' ? ' (Legacy)' : ''}
						  </option>
						))}
					  {/* A saved agent can hold a model that is no longer offered
					      (retired, or an Ollama tag). Keep it selectable so opening
					      the editor doesn't silently rewrite the agent's model. */}
					  {agent.llm_model &&
					   !llmModels.some(m => m.id === agent.llm_model) && (
						<option value={agent.llm_model}>
						  {agent.llm_model}
						  {retiredModels[agent.llm_model] ? ' (discontinued)' : ''}
						</option>
					  )}
					</select>

					{retiredModels[agent.llm_model] && (
					  <p className="text-xs text-red-600 mt-1">
						This model has been discontinued by the provider and calls will
						fail. Switch to <strong>{retiredModels[agent.llm_model]}</strong>.
					  </p>
					)}
				  </div>

				  <div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  TTS Provider
					</label>
					<select
					  value={agent.tts_provider}
					  onChange={(e) => handleCustomTTSProviderChange(e.target.value)}
					  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					>
					  <option value="elevenlabs">ElevenLabs TTS</option>
					  <option value="deepgram">Deepgram Aura TTS</option>
					  <option value="openai">OpenAI TTS</option>
					  <option value="azure">Azure TTS</option>
					  <option value="kokoro">Kokoro (English only) (Local)</option>
					</select>
				  </div>
				</div>

				{/* Ollama Base URL — shown only when Ollama is selected */}
				{(agent.llm_provider === 'ollama') && (
				  <div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  Ollama Server URL
					</label>
					{isReadOnly ? (
					  <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-400 italic">
						Hidden in read-only mode
					  </div>
					) : (
					  <input
						type="text"
						value={agent.ollama_base_url || ''}
						onChange={(e) => setAgent({ ...agent, ollama_base_url: e.target.value })}
						className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
						placeholder="http://192.168.1.100:11434/v1"
					  />
					)}
					<p className="text-xs text-gray-500 mt-1">
					  Full URL to your Ollama instance including <code className="bg-gray-100 px-1 rounded">/v1</code> suffix
					</p>
				  </div>
				)}

				{/* Kokoro Base URL — shown only when Kokoro TTS is selected */}
				{agent.tts_provider === 'kokoro' && (
				  <div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  Kokoro Server URL
					</label>
					{isReadOnly ? (
					  <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-400 italic">
						Hidden in read-only mode
					  </div>
					) : (
					  <input
						type="text"
						value={agent.kokoro_base_url || ''}
						onChange={(e) => setAgent({ ...agent, kokoro_base_url: e.target.value })}
						className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
						placeholder="http://5.19.9.4:8880"
					  />
					)}
					<p className="text-xs text-gray-500 mt-1">
					  URL to your Kokoro TTS server — no <code className="bg-gray-100 px-1 rounded">/v1</code> suffix needed
					</p>
				  </div>
				)}

				{/* STT (speech-to-text) backend selector */}
				<div>
				  <label className="block text-sm font-medium text-gray-700 mb-1">
					STT Provider
				  </label>
				  <select
					value={agent.stt_provider || 'soniox'}
					onChange={(e) => setAgent({ ...agent, stt_provider: e.target.value })}
					className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
				  >
					<option value="soniox">Soniox (Cloud, multilingual)</option>
					<option value="assemblyai">AssemblyAI (Cloud, streaming)</option>
					<option value="elevenlabs">ElevenLabs Scribe (Cloud, multilingual)</option>
					<option value="sherpa">Sherpa GPU (Self-hosted, English)</option>
					<option value="sherpa_ml">Multilingual (Self-hosted GPU)</option>
					<option value="whisper">Whisper (Self-hosted, telephony)</option>
				  </select>
				  {agent.stt_provider === 'assemblyai' && (
					<p className="text-xs text-gray-500 mt-1">
					  Requires an AssemblyAI API key set on the <strong>API Keys</strong> page.
					</p>
				  )}
				  {agent.stt_provider === 'elevenlabs' && (
					<p className="text-xs text-gray-500 mt-1">
					  Uses your <strong>ElevenLabs</strong> API key (the same one as ElevenLabs TTS).
					</p>
				  )}
				</div>

				{/* Self-hosted STT server URL — shown for sherpa (English) or multilingual */}
				{(agent.stt_provider === 'sherpa' || agent.stt_provider === 'sherpa_ml' || agent.stt_provider === 'whisper') && (
				  <div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  STT Server URL
					</label>
					{isReadOnly ? (
					  <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-400 italic">
						Hidden in read-only mode
					  </div>
					) : (
					  <input
						type="text"
						value={agent.stt_url || ''}
						onChange={(e) => setAgent({ ...agent, stt_url: e.target.value })}
						className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
						placeholder="ws://10.0.0.5:6006"
					  />
					)}
					<p className="text-xs text-gray-500 mt-1">
					  WebSocket address of your sherpa-onnx GPU STT server (host:port), e.g. <code className="bg-gray-100 px-1 rounded">ws://10.0.0.5:6006</code>
					</p>
				  </div>
				)}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Voice
                  </label>
                  {agent.tts_provider === 'elevenlabs' ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {ELEVENLABS_VOICES.map(voice => (
                          <button
                            key={voice.id}
                            onClick={() => setAgent({ ...agent, custom_voice: voice.id })}
                            className={`p-3 rounded-lg border-2 text-center transition-all ${
                              agent.custom_voice === voice.id
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium">{voice.name}</div>
                            <div className="text-xs text-gray-500">{voice.desc}</div>
                          </button>
                        ))}
                      </div>

                      {/* ElevenLabs output language — controls what language the agent speaks */}
                      <div className="pt-2 border-t">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Output Language
                        </label>
                        <select
                          value={agent.tts_language || 'en'}
                          onChange={(e) => setAgent({ ...agent, tts_language: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          {ELEVENLABS_LANGUAGES.map(lang => (
                            <option key={lang.code} value={lang.code}>{lang.name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Forces ElevenLabs to speak in this language. Uses <code className="bg-gray-100 px-1 rounded">eleven_turbo_v2_5</code> which supports 32 languages.
                          Separate from the STT language hints above.
                        </p>
                      </div>
                    </div>
                   ) : agent.tts_provider === 'deepgram' ? (
						<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
						  {DEEPGRAM_VOICES.map(voice => (
							<button
							  key={voice.id}
							  onClick={() => setAgent({ ...agent, custom_voice: voice.id })}
							  className={`p-3 rounded-lg border-2 text-center transition-all ${
								agent.custom_voice === voice.id
								  ? 'border-blue-500 bg-blue-50'
								  : 'border-gray-200 hover:border-gray-300'
							  }`}
							>
							  <div className="font-medium">{voice.name}</div>
							  <div className="text-xs text-gray-500">{voice.desc}</div>
							</button>
						  ))}
						</div>
					  ) : agent.tts_provider === 'kokoro' ? (
						<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
						  {KOKORO_VOICES.map(voice => (
							<button
							  key={voice.id}
							  onClick={() => setAgent({ ...agent, custom_voice: voice.id })}
							  className={`p-3 rounded-lg border-2 text-center transition-all ${
								agent.custom_voice === voice.id
								  ? 'border-green-500 bg-green-50'
								  : 'border-gray-200 hover:border-gray-300'
							  }`}
							>
							  <div className="font-medium">{voice.name}</div>
							  <div className="text-xs text-gray-500">{voice.desc}</div>
							</button>
						  ))}
						</div>
					  ) : (
						<div className="grid grid-cols-2 md:grid-cols-5 gap-3">
						  {OPENAI_VOICES.map(voice => (
                        <button
                          key={voice.id}
                          onClick={() => setAgent({ ...agent, custom_voice: voice.id })}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            agent.custom_voice === voice.id
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium">{voice.name}</div>
                          <div className="text-xs text-gray-500">{voice.desc}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Language Hints (for STT)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGES.filter(l => l.code !== 'multi').map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => toggleLanguageHint(lang.code)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          (agent.language_hints || []).includes(lang.code)
                            ? 'bg-purple-100 text-purple-800 border-2 border-purple-300'
                            : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:border-gray-300'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Select languages the caller might use. Helps improve speech recognition accuracy.
                  </p>
                </div>
              </div>
            )}
			
			{/* LiveKit Provider Config */}
			{agent.provider === 'livekit' && (
			  <div className="space-y-6">
				<div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
				  <div className="flex items-center gap-2 mb-2">
					<span className="text-xl">🎬</span>
					<h4 className="font-semibold text-cyan-900">LiveKit Voice Agent</h4>
				  </div>
				  <p className="text-sm text-cyan-700">
					Self-hosted LiveKit server with Agents SDK. Configure STT, TTS, and LLM providers.
				  </p>
				</div>

				{/* LiveKit Server URL */}
				<div>
				  <label className="block text-sm font-medium text-gray-700 mb-1">
					LiveKit Server URL
				  </label>
				  <input
					type="text"
					value={agent.livekit_url || ''}
					onChange={(e) => setAgent({ ...agent, livekit_url: e.target.value })}
					placeholder="wss://your-livekit-server.com"
					className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
				  />
				  <p className="text-xs text-gray-500 mt-1">
					Your self-hosted LiveKit server WebSocket URL
				  </p>
				</div>

				{/* Agent Name */}
				<div>
				  <label className="block text-sm font-medium text-gray-700 mb-1">
					Agent Name
				  </label>
				  <input
					type="text"
					value={agent.livekit_agent_name || 'voice-agent'}
					onChange={(e) => setAgent({ ...agent, livekit_agent_name: e.target.value })}
					placeholder="voice-agent"
					className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
				  />
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				  {/* STT Provider */}
				  <div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  STT Provider
					</label>
					<select
					  value={agent.livekit_stt_provider}
					  onChange={(e) => setAgent({ ...agent, livekit_stt_provider: e.target.value })}
					  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					>
					  {LIVEKIT_STT_PROVIDERS.map(p => (
						<option key={p.id} value={p.id}>{p.name}</option>
					  ))}
					</select>
				  </div>

				  {/* TTS Provider */}
				  <div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  TTS Provider
					</label>
					<select
					  value={agent.livekit_tts_provider}
					  onChange={(e) => handleLivekitTTSProviderChange(e.target.value)}
					  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					>
					  {LIVEKIT_TTS_PROVIDERS.map(p => (
						<option key={p.id} value={p.id}>{p.name}</option>
					  ))}
					</select>
				  </div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				  {/* LLM Model */}
				  <div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  LLM Model
					</label>
					<select
					  value={agent.livekit_model}
					  onChange={(e) => setAgent({ ...agent, livekit_model: e.target.value })}
					  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					>
					  <option value="gpt-4o-mini">GPT-4o Mini</option>
					  <option value="gpt-4o">GPT-4o</option>
					  <option value="gpt-4-turbo">GPT-4 Turbo</option>
					  <option value="claude-3-sonnet">Claude 3 Sonnet</option>
					  <option value="claude-3-haiku">Claude 3 Haiku</option>
					</select>
				  </div>

				  {/* Language */}
				  <div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  Language
					</label>
					<select
					  value={agent.livekit_language || 'en'}
					  onChange={(e) => setAgent({ ...agent, livekit_language: e.target.value })}
					  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					>
					  {LANGUAGES.filter(l => l.code !== 'multi').map(lang => (
						<option key={lang.code} value={lang.code}>{lang.name}</option>
					  ))}
					</select>
				  </div>
				</div>

				{/* Voice Selection - Dynamic based on TTS Provider */}
				<div>
				  <label className="block text-sm font-medium text-gray-700 mb-3">
					Voice ({agent.livekit_tts_provider === 'openai' ? 'OpenAI' : 
							agent.livekit_tts_provider === 'elevenlabs' ? 'ElevenLabs' :
							agent.livekit_tts_provider === 'cartesia' ? 'Cartesia' :
							agent.livekit_tts_provider === 'azure' ? 'Azure' :
							agent.livekit_tts_provider === 'deepgram' ? 'Deepgram Aura' : 'OpenAI'} Voices)
				  </label>
				  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
					{getLivekitVoices().map(voice => (
					  <button
						key={voice.id}
						type="button"
						onClick={() => setAgent({ ...agent, livekit_voice: voice.id })}
						className={`p-3 rounded-lg border-2 text-center transition-all ${
						  agent.livekit_voice === voice.id
							? 'border-cyan-500 bg-cyan-50'
							: 'border-gray-200 hover:border-gray-300'
						}`}
					  >
						<div className="font-medium text-sm">{voice.name}</div>
						<div className="text-xs text-gray-500">{voice.desc}</div>
					  </button>
					))}
				  </div>
				</div>

				{/* Language Hints for STT */}
				<div>
				  <label className="block text-sm font-medium text-gray-700 mb-3">
					Language Hints (for STT)
				  </label>
				  <div className="flex flex-wrap gap-2">
					{LANGUAGES.filter(l => l.code !== 'multi').slice(0, 20).map(lang => (
					  <button
						key={lang.code}
						onClick={() => toggleLanguageHint(lang.code)}
						className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
						  (agent.language_hints || []).includes(lang.code)
							? 'bg-cyan-100 text-cyan-800 border-2 border-cyan-300'
							: 'bg-gray-100 text-gray-600 border-2 border-transparent hover:border-gray-300'
						}`}
					  >
						{lang.name}
					  </button>
					))}
				  </div>
				  <p className="text-xs text-gray-500 mt-2">
					Select languages the caller might use. Helps improve speech recognition accuracy.
				  </p>
				</div>
			  </div>
			)}
			
            {/* Intent-IVR Config */}
            {agent.provider === 'intent-ivr' && (
              <div className="space-y-6">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">📞</span>
                    <h4 className="font-semibold text-amber-900">Intent-Based IVR</h4>
                  </div>
                  <p className="text-sm text-amber-700">
                    Uses pre-recorded audio with intent matching for lowest cost. Configure intents, flows, and audio segments in the IVR Config section.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fallback TTS Provider
                    </label>
                    <select
                      value={agent.tts_provider}
                      onChange={(e) => setAgent({ ...agent, tts_provider: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="elevenlabs">ElevenLabs TTS</option>
                      <option value="deepgram">Deepgram Aura TTS</option>
                      <option value="openai">OpenAI TTS</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Used when pre-recorded audio is not available
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fallback Voice
                    </label>
                    <select
                      value={agent.custom_voice}
                      onChange={(e) => setAgent({ ...agent, custom_voice: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {(agent.tts_provider === 'elevenlabs' 
                        ? ELEVENLABS_VOICES 
                        : agent.tts_provider === 'deepgram'
                          ? DEEPGRAM_VOICES
                          : OPENAI_VOICES
                      ).map(v => (
                        <option key={v.id} value={v.id}>{v.name} - {v.desc}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Supported Languages
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGES.filter(l => l.code !== 'multi').map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => toggleLanguageHint(lang.code)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          (agent.language_hints || []).includes(lang.code)
                            ? 'bg-amber-100 text-amber-800 border-2 border-amber-300'
                            : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:border-gray-300'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>

                {!isNew && (
                  <div className="pt-4 border-t">
                    <div className="flex flex-wrap gap-3">
                      <Link
                        to={`/agents/${id}/ivr`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                      >
                        <Settings className="w-4 h-4" />
                        Configure Intents
                      </Link>
                      <Link
                        to={`/agents/${id}/audio`}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50"
                      >
                        <Volume2 className="w-4 h-4" />
                        Audio Library
                      </Link>
                      <Link
                        to={`/agents/${id}/flows`}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50"
                      >
                        <Zap className="w-4 h-4" />
                        Flows
                      </Link>
                      <Link
                        to={`/agents/${id}/scripts`}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50"
                      >
                        <ClipboardList className="w-4 h-4" />
                        Outbound Scripts
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Behavior Tab */}
        {activeTab === 'behavior' && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-2">
                    <Thermometer className="w-4 h-4" />
                    Temperature
                  </div>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={agent.temperature}
                  onChange={(e) => setAgent({ ...agent, temperature: parseFloat(e.target.value) })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Focused (0)</span>
                  <span className="font-medium">{agent.temperature}</span>
                  <span>Creative (1)</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Tokens
                </label>
                <input
                  type="number"
                  value={agent.max_tokens}
                  onChange={(e) => setAgent({ ...agent, max_tokens: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-2">
                    <Mic className="w-4 h-4" />
                    VAD Threshold
                  </div>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={agent.vad_threshold}
                  onChange={(e) => setAgent({ ...agent, vad_threshold: parseFloat(e.target.value) })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Sensitive (0)</span>
                  <span className="font-medium">{agent.vad_threshold}</span>
                  <span>Less sensitive (1)</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Voice Activity Detection sensitivity
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Silence Duration (ms)
                  </div>
                </label>
                <input
                  type="number"
                  value={agent.silence_duration_ms}
                  onChange={(e) => setAgent({ ...agent, silence_duration_ms: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Silence after the caller stops before the bot responds (VAD). Higher = more patient — won't interrupt someone pausing to think. ~1000 for phone calls; 500 for snappy.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Outbound Tab - AMD Settings */}
        {activeTab === 'outbound' && (
          <div className="p-6 space-y-6">
            {/* AMD Section */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Answering Machine Detection (AMD)</h3>
                  <p className="text-sm text-gray-500">
                    Detect voicemail, IVR, or human when making outbound calls
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agent.amd_enabled}
                    onChange={(e) => setAgent({ ...agent, amd_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {agent.amd_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>

              {agent.amd_enabled && (
                <div className="space-y-4 pt-4 border-t">
                  {/* AMD Action */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      When voicemail is detected:
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="amd_action"
                          value="hangup"
                          checked={agent.amd_action === 'hangup'}
                          onChange={(e) => setAgent({ ...agent, amd_action: e.target.value })}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Hang Up</div>
                          <div className="text-sm text-gray-500">Disconnect immediately when voicemail is detected</div>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="amd_action"
                          value="leave_message"
                          checked={agent.amd_action === 'leave_message'}
                          onChange={(e) => setAgent({ ...agent, amd_action: e.target.value })}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Leave Message</div>
                          <div className="text-sm text-gray-500">Wait for beep and leave a voicemail message</div>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="amd_action"
                          value="continue"
                          checked={agent.amd_action === 'continue'}
                          onChange={(e) => setAgent({ ...agent, amd_action: e.target.value })}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Continue Conversation</div>
                          <div className="text-sm text-gray-500">Let the agent talk to the voicemail as if it were a person</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Voicemail Message */}
                  {agent.amd_action === 'leave_message' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Voicemail Message
                      </label>
                      <textarea
                        value={agent.amd_message || ''}
                        onChange={(e) => setAgent({ ...agent, amd_message: e.target.value })}
                        placeholder="Hi, this is a callback from Acme Corp. Please call us back at 555-1234. Thank you!"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This message will be played after the beep is detected
                      </p>
                    </div>
                  )}

                  {/* AMD Timeout */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Detection Timeout (ms)
                    </label>
                    <input
                      type="number"
                      value={agent.amd_timeout || 8000}
                      onChange={(e) => setAgent({ ...agent, amd_timeout: parseInt(e.target.value) })}
                      min={3000}
                      max={15000}
                      step={1000}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum time to wait for detection (3-15 seconds). After this, assumes human.
                    </p>
                  </div>

                  {/* Info box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">How AMD Works</p>
                        <ul className="list-disc list-inside space-y-1 text-blue-700">
                          <li>Analyzes the first few seconds of audio after call connects</li>
                          <li>Detects voicemail phrases like "leave a message", "after the beep"</li>
                          <li>Identifies IVR menus ("press 1 for sales")</li>
                          <li>Short responses like "Hello?" indicate a human</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Outbound API Info */}
            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Outbound Call API</h3>
              <p className="text-sm text-gray-500 mb-4">
                Use this endpoint to trigger outbound calls with this agent
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-100">
{`POST /outbound
{
  "phone_number": "+15551234567",
  "agent_id": "${id || 'your-agent-id'}",
  "variables": {
    "customer_name": "John Smith",
    "order_id": "ORD-12345"
  }
}`}
                </pre>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Variables are automatically injected into the agent's instructions
              </p>
            </div>
          </div>
        )}

        {/* Functions Tab */}
        {activeTab === 'functions' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-gray-900">Agent Functions</h3>
                <p className="text-sm text-gray-500">
                  API integrations the agent can call during conversations
                </p>
              </div>
              <button
                onClick={() => {
                  setEditingFunction(null);
                  setShowFunctionModal(true);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
              >
                <Zap className="w-4 h-4" />
                Add Function
              </button>
            </div>

            {functions.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Zap className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No functions yet</h3>
                <p className="text-gray-500 mb-4">
                  Add functions to let your agent call external APIs
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {functions.map((func, index) => (
                  <div
                    key={func.id || index}
                    className="p-4 border rounded-lg hover:border-primary-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{func.name}</h4>
                        <p className="text-sm text-gray-500">{func.description}</p>
                        {func.api_url && (
                          <p className="text-xs text-gray-400 mt-1">
                            {func.api_method || 'POST'} {func.api_url}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingFunction(func);
                            setShowFunctionModal(true);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
		
		{/* API Keys Tab */}
		{activeTab === 'apikeys' && (
		  <div className="p-6 space-y-6">
			<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
			  <p className="text-sm text-blue-700">
				<strong>Optional:</strong> Set per-agent API keys to override your global tenant keys. 
				Leave blank to use your global API keys from Settings → API Keys.
			  </p>
			</div>
			
			<div className="grid gap-4">
			  {[
				{ key: 'openai', label: 'OpenAI', icon: '🟢', placeholder: 'sk-...' },
				{ key: 'deepgram', label: 'Deepgram', icon: '🔵', placeholder: 'Enter Deepgram API key' },
				{ key: 'soniox', label: 'Soniox', icon: '🟡', placeholder: 'Enter Soniox API key' },
				{ key: 'assemblyai', label: 'AssemblyAI', icon: '🧠', placeholder: 'Enter AssemblyAI API key' },
				{ key: 'groq', label: 'Groq', icon: '🟠', placeholder: 'gsk_...' },
				{ key: 'elevenlabs', label: 'ElevenLabs', icon: '🎙️', placeholder: 'Enter ElevenLabs API key' },
				{ key: 'cartesia', label: 'Cartesia', icon: '🔮', placeholder: 'Enter Cartesia API key' },
				{ key: 'azure_speech_key', label: 'Azure Speech Key', icon: '☁️', placeholder: 'Enter Azure Speech key' },
				{ key: 'azure_speech_region', label: 'Azure Speech Region', icon: '☁️', placeholder: 'e.g. eastus' },
			  ].map(provider => (
				<div key={provider.key} className="flex items-center gap-3">
				  <span className="text-xl w-8 text-center">{provider.icon}</span>
				  <div className="flex-1">
					<label className="block text-sm font-medium text-gray-700 mb-1">
					  {provider.label}
					</label>
					<input
					  type="password"
					  value={agent.api_keys?.[provider.key] || ''}
					  onChange={(e) => setAgent({
						...agent,
						api_keys: {
						  ...agent.api_keys,
						  [provider.key]: e.target.value
						}
					  })}
					  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					  placeholder={provider.placeholder}
					/>
				  </div>
				  {agent.api_keys?.[provider.key] && (
					<button
					  onClick={() => {
						const newKeys = { ...agent.api_keys };
						delete newKeys[provider.key];
						setAgent({ ...agent, api_keys: newKeys });
					  }}
					  className="mt-6 text-red-400 hover:text-red-600"
					  title="Clear"
					>
					  ×
					</button>
				  )}
				</div>
			  ))}
			</div>
		  </div>
		)}
      </div>
      </fieldset>

      {/* Function Modal */}
      <FunctionModal
        isOpen={showFunctionModal}
        onClose={() => {
          setShowFunctionModal(false);
          setEditingFunction(null);
        }}
        editingFunction={editingFunction}
        setEditingFunction={setEditingFunction}
        onSave={async () => {
          if (!editingFunction?.name || !editingFunction?.api_url) {
            toast.error('Name and API URL are required');
            return;
          }
          try {
            if (editingFunction.id) {
              // Update existing
              await api.put(`/functions/${editingFunction.id}`, editingFunction);
              setFunctions(functions.map(f => 
                f.id === editingFunction.id ? editingFunction : f
              ));
              toast.success('Function updated');
            } else {
              // Create new
              const res = await api.post(`/functions/agent/${id}`, editingFunction);
              setFunctions([...functions, res.data.function]);
              toast.success('Function created');
            }
            setShowFunctionModal(false);
            setEditingFunction(null);
          } catch (error) {
            toast.error('Failed to save function');
          }
        }}
        onDelete={async () => {
          if (confirm('Delete this function?')) {
            try {
              await api.delete(`/functions/${editingFunction.id}`);
              setFunctions(functions.filter(f => f.id !== editingFunction.id));
              setShowFunctionModal(false);
              setEditingFunction(null);
              toast.success('Function deleted');
            } catch (error) {
              toast.error('Failed to delete function');
            }
          }
        }}
      />
    </div>
  );
}